import React, { useState } from 'react';
import { Menu, Bell, User as UserIcon, Search, ChevronDown, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../middleware/authMiddleware';

const UserNavbar = ({ toggleSidebar, sidebarCollapsed, toggleCollapseSidebar, user }) => {
  const { logout } = useAuth();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  const toggleProfileMenu = () => {
    setShowProfileMenu(!showProfileMenu);
    if (showNotifications) setShowNotifications(false);
  };

  const toggleNotificationMenu = () => {
    setShowNotifications(!showNotifications);
    if (showProfileMenu) setShowProfileMenu(false);
  };

  return (
    <header className="bg-white border-b border-gray-divider z-20">
      <div className="flex items-center justify-between h-16 px-4">
        {/* Left side - Mobile menu toggle and search */}
        <div className="flex items-center">
          <button
            onClick={toggleSidebar}
            className="p-2 rounded-md lg:hidden text-gray-tertiary hover:bg-gray-block"
          >
            <Menu size={20} />
          </button>

          <button
            onClick={toggleCollapseSidebar}
            className="p-2 rounded-md hidden lg:block text-gray-tertiary hover:bg-gray-block"
          >
            <Menu size={20} />
          </button>

          <div className="ml-4 relative max-w-xs">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search size={16} className="text-gray-tertiary" />
            </div>
            <input
              type="text"
              className="pl-10 pr-4 py-2 w-full border border-gray-formField rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-base"
              placeholder="Search..."
            />
          </div>
        </div>

        {/* Right side - notifications and profile */}
        <div className="flex items-center space-x-4">
          {/* Notifications */}
          <div className="relative">
            <button
              onClick={toggleNotificationMenu}
              className="p-2 rounded-full bg-gray-body text-gray-tertiary hover:bg-gray-block relative"
            >
              <Bell size={20} />
              <span className="absolute top-0 right-0 w-2 h-2 bg-supporting-error rounded-full"></span>
            </button>

            {/* Notifications dropdown */}
            {showNotifications && (
              <div className="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-lg py-1 z-30 border border-gray-divider">
                <div className="px-4 py-2 border-b border-gray-divider">
                  <h3 className="text-sm font-semibold text-gray-dark">Notifications</h3>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  <div className="px-4 py-3 hover:bg-gray-block border-b border-gray-divider cursor-pointer">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-primary-base text-white flex items-center justify-center">
                          <Bell size={14} />
                        </div>
                      </div>
                      <div className="ml-3 w-full">
                        <p className="text-sm font-medium text-gray-dark">New Announcement Posted</p>
                        <p className="text-xs text-gray-tertiary mt-1">2 hours ago</p>
                      </div>
                    </div>
                  </div>
                  <div className="px-4 py-3 hover:bg-gray-block">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-supporting-success text-white flex items-center justify-center">
                          <Calendar size={14} />
                        </div>
                      </div>
                      <div className="ml-3 w-full">
                        <p className="text-sm font-medium text-gray-dark">Event Updated: College Day</p>
                        <p className="text-xs text-gray-tertiary mt-1">Yesterday</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="px-4 py-2 border-t border-gray-divider text-center">
                  <Link to="/user/notifications" className="text-xs text-primary-base hover:underline">
                    View all notifications
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* Profile */}
          <div className="relative">
            <button
              onClick={toggleProfileMenu}
              className="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-block"
            >
              <div className="w-8 h-8 rounded-full bg-primary-base text-white flex items-center justify-center">
                {user && user.fullName ? (
                  user.fullName.charAt(0).toUpperCase()
                ) : (
                  <UserIcon size={20} />
                )}
              </div>
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium text-gray-dark">
                  {user ? user.fullName : 'User'}
                </p>
                <p className="text-xs text-gray-tertiary">
                  {user && user.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'Editor'}
                </p>
              </div>
              <ChevronDown size={16} className="hidden md:block text-gray-tertiary" />
            </button>

            {/* Profile dropdown */}
            {showProfileMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-30 border border-gray-divider">
                <Link
                  to="/user/profile"
                  className="block px-4 py-2 text-sm text-gray-semidark hover:bg-gray-block"
                >
                  Your Profile
                </Link>
                <Link
                  to="/user/settings"
                  className="block px-4 py-2 text-sm text-gray-semidark hover:bg-gray-block"
                >
                  Settings
                </Link>
                <button
                  onClick={logout}
                  className="w-full text-left block px-4 py-2 text-sm text-gray-semidark hover:bg-gray-block"
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default UserNavbar;